import ContainerPage from "../../../components/Containers/ContainerPage";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  ADS_CAMPAIGN_STATISTICS_SLIDE_MENU_STATISTICS,
  CHAT_ASK_A_QUESTION_ABOUT_YOUR_CAMPAIGN,
  EMPTY_STATISTICS_FOR_DETAILED_REPORT,
  GENERATING_REPORT,
  GETTING_METRICS,
  REPORT_DATE_GENERATED,
  REPORT_GENERATED_BY_BUZZLY_AI,
} from "../../../i18n/keysTranslations";
import CommonChatMainContainer from "../../../components/CommonChat/CommonChatMainContainer";
import {
  ADS_OBJECT_TYPES,
  BORDER_RADIUS,
  CHAT_CONFIGURATION_ADS_DETAILED_REPORT_ID,
  IS_ADLE_DOMAIN,
  ADLE_DOMAIN,
  REPORT_GENERATED_BY_BUZZLY_AI_LINK,
  THEME_ID_PROJECT,
  TIME_LOADER_GENERATING_REPORT_CAMPAIGN,
} from "../../../utils/constants";
import { hasRoleCapability } from "../../../utils/roleCapabilities";
import { useFormContext } from "react-hook-form";
import { useTranslationApp } from "../../../lib/i18next";
import _ from "lodash";
import ConfigurationReport from "./ConfigurationReport";
import OverviewStatistics from "./OverviewStatistics/OverviewStatistics";
import TableStatistics from "./TableStatistics/TableStatistics";
import PerformingAssets from "./PerformingAssets/PerformingAssets";
import LinearLoaderWithTime from "../../../components/Loaders/LinearLoaderWithTime";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { getFormattedCampaign } from "../../../utils/string";
import SummaryTetx from "./SumaryText/SummaryTetx";
import CampaignLearningsSection from "./CampaignLearningsSection/CampaignLearningsSection";
import LogsSection from "./LogsSection/LogsSection";
import { startGetCampaignLogs } from "../../../actions/adsCampaigns";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { useSearchParams } from "react-router-dom";
import LogoBuzzly from "../../../assets/img/logo.png";
import { useTheme } from "@mui/material";
import { BOX_SHADOW_CHAT, INPUT_COLOR_BORDER } from "../../../utils/colors";
import { startOfDay, endOfDay } from "date-fns";
import CommonChatMessageInput from "../../../components/CommonChat/CommonChatMessageInput";
import { getNameBusinessTheme } from "../../../actions/getters";
import { AdleLogoLoader } from "../../../assets/svg/AdleLogoLoader";
import { formatReportData } from "../../../features/reports/utils/formatReportData";
import { optionsAdsCampaignBreakdownsReport } from "../../../utils/options";
import { getFromStatisticsAdsCampaign } from "../../../services/adsCampaigns";
import html2canvas from "html2canvas";
import i18next from "i18next";
import { runDuringPdfDomCapture } from "../../../utils/pdfDomCapture";
import { useRebrandBanner } from "../../../hooks/useRebrandBanner";

const captureElementToPngDataUri = async (element, scale = 2) => {
  if (!element) return null;
  try {
    const canvas = await html2canvas(element, {
      backgroundColor: "#ffffff",
      scale,
      useCORS: true,
      allowTaint: true,
      imageTimeout: 15000,
      logging: false,
      onclone: (clonedDoc) => {
        const clonedElement = clonedDoc.querySelector(
          `[data-pdf-capture-id="${element.dataset.pdfCaptureId}"]`
        );
        if (clonedElement) {
          clonedElement.style.transform = "none";
        }
      },
    });
    return canvas.toDataURL("image/png");
  } catch (error) {
    console.error("Error capturing chart card snapshot:", error);
    return null;
  }
};

const waitForElementImages = async (element) => {
  if (!element) return;
  const images = Array.from(element.querySelectorAll("img"));
  if (!images.length) return;

  await Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          const done = () => {
            img.removeEventListener("load", done);
            img.removeEventListener("error", done);
            resolve();
          };
          img.addEventListener("load", done, { once: true });
          img.addEventListener("error", done, { once: true });
        })
    )
  );
};

const ContainerStatistics = () => {
  const {
    campaign,
    isGeneratingReport,
    isEmptyReport,
    isGeneratedReport,
    reportConfiguration,
    getAggregatedMetrics,
    watch,
  } = useFormContext();

  const theme = useTheme();

  const [historicalStatistics, setHistoricalStatistics] = useState(
    campaign?.historicalStatistics?.historical || {}
  );
  const [summaryAiSettled, setSummaryAiSettled] = useState(false);
  const [learningsAiSettled, setLearningsAiSettled] = useState(false);

  const [logs, setLogs] = useState({});
  const [isLoadingLogs, setIsLoadingLogs] = useState(true);
  const [summaryTextForPdf, setSummaryTextForPdf] = useState("");
  const [learningsForPdf, setLearningsForPdf] = useState([]);

  const [isFloatingChatOpen, setIsFloatingChatOpen] = useState(false);
  const chatSendHandlerRef = useRef(null);

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const user = useSelectorApp((state) => state.auth.user);
  const [params] = useSearchParams();
  const campaignID = params.get("campaignID");
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());
  const { totalTopOffsetPx } = useRebrandBanner();

  const platforms = useMemo(
    () => getFromStatisticsAdsCampaign(campaign).platforms,
    [campaign]
  );

  const getInitialLogs = useCallback(async () => {
    if (!campaignID) return;

    try {
      setIsLoadingLogs(true);
      const response = await dispatch(
        startGetCampaignLogs({
          campaignID,
          onChangeLastKey: () => {},
        })
      );

      if (response.ok) {
        const { data = {} } = response;
        setLogs(data);
      }
    } catch (error) {
      console.error("Error getting logs:", error);
    } finally {
      setIsLoadingLogs(false);
    }
  }, [campaignID, dispatch]);

  const getFilteredLogs = useCallback(() => {
    if (
      !reportConfiguration?.startDateTime ||
      !reportConfiguration?.endDateTime
    ) {
      return Object.values(logs);
    }

    const logsArray = Object.values(logs).sort(
      (a, b) => b.creationTime - a.creationTime
    );

    const startDateTime = startOfDay(
      new Date(reportConfiguration.startDateTime)
    );
    const endDateTime = endOfDay(new Date(reportConfiguration.endDateTime));

    return logsArray.filter((log) => {
      const logCreationTime = new Date(log.creationTime);
      const isInsightLogAndNotIsDiscarded =
        log?.changes?.[0]?.objectType === ADS_OBJECT_TYPES.insight &&
        !log?.changes?.[0]?.newValue?.isDiscarded;

      if (!isInsightLogAndNotIsDiscarded) {
        return false;
      }
      return logCreationTime >= startDateTime && logCreationTime <= endDateTime;
    });
  }, [
    logs,
    reportConfiguration?.startDateTime,
    reportConfiguration?.endDateTime,
  ]);

  useEffect(() => {
    const { current: dataProcessed } = getAggregatedMetrics({
      forcedBreakdown: "daily",
      includePreviousPeriod: false,
    });
    setHistoricalStatistics(dataProcessed);
    // eslint-disable-next-line
  }, [campaign?.historicalStatistics?.historical]);

  useEffect(() => {
    getInitialLogs();
  }, [getInitialLogs]);

  useEffect(() => {
    if (isGeneratingReport || isEmptyReport) {
      setSummaryAiSettled(false);
      setLearningsAiSettled(false);
    }
  }, [isGeneratingReport, isEmptyReport]);

  const filteredLogs = getFilteredLogs();
  const selectedBreakdown = watch("breakdown");
  const breakdownOption = optionsAdsCampaignBreakdownsReport.find(
    (opt) => opt.value === selectedBreakdown
  );
  const selectedBreakdownLabel = breakdownOption
    ? t(breakdownOption.label)
    : "-";

  const getReportDataForDownload = useCallback(async () => {
    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });

    const captureOrder = [
      "overview-trends-card",
      "overview-cpm-card",
      "overview-cpc-card",
      "overview-cpa-card",
    ];

    const chartCards = captureOrder
      .map((id) =>
        document.querySelector(
          `#overview-section [data-pdf-capture-id="${id}"]`
        )
      )
      .filter(Boolean);

    const overviewChartSnapshots = (
      await Promise.all(
        chartCards.map(async (cardElement) => {
          await waitForElementImages(cardElement);
          return captureElementToPngDataUri(cardElement, 2);
        })
      )
    ).filter(Boolean);

    const tableCards = platforms
      .map((platform) =>
        document.querySelector(
          `#metrics-section [data-pdf-capture-id="metrics-table-card-${platform}"]`
        )
      )
      .filter(Boolean);

    const metricsTableSnapshots = (
      await runDuringPdfDomCapture(async () =>
        Promise.all(
          tableCards.map(async (cardElement) => {
            await waitForElementImages(cardElement);
            return captureElementToPngDataUri(cardElement, 3);
          })
        )
      )
    ).filter(Boolean);

    const assetsCard = document.querySelector(
      '#performing-assets-section [data-pdf-capture-id="performing-assets-card"]'
    );
    const performingSnapshot = assetsCard
      ? await runDuringPdfDomCapture(async () => {
          await waitForElementImages(assetsCard);
          return captureElementToPngDataUri(assetsCard, 2);
        })
      : null;
    const performingAssetsSnapshots = performingSnapshot
      ? [performingSnapshot]
      : [];

    return formatReportData({
      campaign,
      reportConfiguration,
      breakdown: selectedBreakdown,
      breakdownLabel: selectedBreakdownLabel,
      filteredLogs,
      logoUrl: !THEME_ID_PROJECT ? LogoBuzzly : theme.logo,
      businessName: NAME_BUSINESS,
      reportGeneratedByLink: REPORT_GENERATED_BY_BUZZLY_AI_LINK,
      footerDateText: t(REPORT_DATE_GENERATED, { date: new Date() }),
      footerGeneratedByText: t(REPORT_GENERATED_BY_BUZZLY_AI, {
        NAME_BUSINESS: THEME_ID_PROJECT ? NAME_BUSINESS : ADLE_DOMAIN,
      }),
      showFooterLink: !THEME_ID_PROJECT,
      summaryText: summaryTextForPdf,
      learnings: learningsForPdf,
      overviewChartSnapshots,
      metricsTableSnapshots,
      performingAssetsSnapshots,
      overviewSectionTitle: t(ADS_CAMPAIGN_STATISTICS_SLIDE_MENU_STATISTICS),
      locale: i18next.language,
      t,
    });
  }, [
    campaign,
    reportConfiguration,
    selectedBreakdown,
    selectedBreakdownLabel,
    filteredLogs,
    theme.logo,
    t,
    NAME_BUSINESS,
    summaryTextForPdf,
    learningsForPdf,
    platforms,
  ]);

  const isPdfReportDataComplete = useMemo(() => {
    if (isGeneratingReport || isEmptyReport) return false;
    if (isLoadingLogs) return false;
    if (!summaryAiSettled || !learningsAiSettled) return false;
    return true;
  }, [
    isGeneratingReport,
    isEmptyReport,
    isLoadingLogs,
    summaryAiSettled,
    learningsAiSettled,
  ]);

  return (
    <ContainerPage
      mode="padding"
      hasMarginBottom={true}
      sx={{
        width: "100%",
        minHeight: `calc(100vh - ${totalTopOffsetPx}px)`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: "100%",
        }}
      >
        <ConfigurationReport
          isEmptyReport={isEmptyReport}
          getReportDataForDownload={getReportDataForDownload}
          isPdfReportReady={isPdfReportDataComplete}
        />

        {isEmptyReport && !isGeneratingReport && (
          <Typography variant="body1" align="center">
            {t(EMPTY_STATISTICS_FOR_DETAILED_REPORT)}
          </Typography>
        )}

        {!isEmptyReport && isGeneratingReport && isGeneratedReport && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "calc(100vh - 200px)",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {IS_ADLE_DOMAIN && <AdleLogoLoader />}
            <LinearLoaderWithTime
              labelSide="bottom"
              time={TIME_LOADER_GENERATING_REPORT_CAMPAIGN}
              max={99}
              labels={[t(GETTING_METRICS), t(GENERATING_REPORT)]}
              sx={{
                maxWidth: 600,
              }}
            />
          </Box>
        )}
        {!isEmptyReport && !isGeneratingReport && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              width: "100%",
            }}
          >
            <SummaryTetx
              onSummaryGenerated={setSummaryTextForPdf}
              onSummaryFetchSettled={() => setSummaryAiSettled(true)}
            />
            <CampaignLearningsSection
              onLearningsGenerated={setLearningsForPdf}
              onLearningsFetchSettled={() => setLearningsAiSettled(true)}
            />
            <OverviewStatistics
              filteredLogs={filteredLogs}
              isLoadingLogs={isLoadingLogs}
            />
            <LogsSection
              filteredLogs={filteredLogs}
              isLoadingLogs={isLoadingLogs}
            />
            <TableStatistics />
            <PerformingAssets />
          </Box>
        )}
      </Box>
      {/* Floating chat based on BottomBar.jsx */}
      {!_.isEmpty(campaign) &&
        !isGeneratingReport &&
        !isEmptyReport &&
        hasRoleCapability(user?.role, "canUseCampaignAgent") && (
          <>
            <Box
              sx={{
                boxShadow: isFloatingChatOpen ? BOX_SHADOW_CHAT : 0,
                borderTopLeftRadius: "12px !important",
                borderTopRightRadius: "12px !important",
                position: "fixed",
                bottom: 77,
                right: 16,
                width: "calc(50vw - 1px)",
                minWidth: 400,
                maxWidth: 900,
                height: 500,
                zIndex: 1300,
                visibility: isFloatingChatOpen ? "visible" : "hidden",
                pointerEvents: isFloatingChatOpen ? "auto" : "none",
              }}
            >
              <CommonChatMainContainer
                statisticsView={true}
                embedded
                hideLauncher
                showOnlyMessages
                controlledOpen={isFloatingChatOpen}
                onChangeOpen={setIsFloatingChatOpen}
                exposeSendMessage={(handler) =>
                  (chatSendHandlerRef.current = handler)
                }
                showPreviewOptimization={false}
                chatConfigurationID={CHAT_CONFIGURATION_ADS_DETAILED_REPORT_ID}
                chatContext={{
                  campaign: getFormattedCampaign({
                    data: {
                      ...(campaign || {}),
                      historicalStatistics: {
                        ...(campaign?.historicalStatistics || {}),
                        historical: historicalStatistics,
                      },
                    },
                    format: "save",
                  }),
                  reportConfiguration: {
                    startDateTime: reportConfiguration.startDateTime,
                    endDateTime: reportConfiguration.endDateTime,
                  },
                }}
                startMode="messageInput"
                placeholderInput={CHAT_ASK_A_QUESTION_ABOUT_YOUR_CAMPAIGN}
                headerTitle={CHAT_ASK_A_QUESTION_ABOUT_YOUR_CAMPAIGN}
                sx={{
                  position: "relative",
                  bottom: 0,
                  right: 0,
                  width: "100%",
                  height: "100%",
                  boxShadow: 0,
                  border: 0,
                }}
                contentSx={{
                  minHeight: "88%",
                  maxHeight: "88%",
                  width: "100%",
                  height: "100%",
                }}
              />
            </Box>

            {/* Floating bubble with input and buttons */}
            <Box
              sx={{
                position: "fixed",
                height: 56,
                bottom: 22,
                right: 16,
                zIndex: 1300,
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: "background.paper",
                padding: "11px 6px 6px 6px",
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                boxShadow:
                  "0 1px 2px rgba(0,0,0,0.03), 0 -1px 2px rgba(0,0,0,0.03)",
                width: "calc(50vw - 1px)",
                minWidth: 400,
                maxWidth: 900,
                transition: "box-shadow 0.1s ease-out",
                borderTopLeftRadius: !isFloatingChatOpen
                  ? `${BORDER_RADIUS * 4}px !important`
                  : "0px !important",
                borderTopRightRadius: !isFloatingChatOpen
                  ? `${BORDER_RADIUS * 4}px !important`
                  : "0px !important",
                borderBottomLeftRadius: `${BORDER_RADIUS * 4}px !important`,
                borderBottomRightRadius: `${BORDER_RADIUS * 4}px !important`,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 300,
                }}
                onClick={() => setIsFloatingChatOpen(true)}
              >
                <CommonChatMessageInput
                  as="MessageInput"
                  multiline={false}
                  disabled={false}
                  embedded={true}
                  onSendMessage={(text) => chatSendHandlerRef.current?.(text)}
                  showSendFileButton={false}
                  placeholderInput={CHAT_ASK_A_QUESTION_ABOUT_YOUR_CAMPAIGN}
                  sx={{
                    boxShadow: "none",
                    padding: "12px 10px",
                    "& .MuiInputBase-root": {
                      border: "none !important",
                      boxShadow: "none !important",
                      backgroundColor: "transparent !important",
                    },
                    "& .MuiInputBase-input": {
                      backgroundColor: "transparent !important",
                      "&:focus": {
                        backgroundColor: "transparent !important",
                      },
                    },
                  }}
                />
              </Box>
            </Box>
          </>
        )}
    </ContainerPage>
  );
};

export default ContainerStatistics;
